import { useEffect, useCallback, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { NavIcon } from './shared';

// PRD.215 — Global App Launcher Overlay
// 4-dots launcher trigger + large overlay/panel above a dimmed background.

// ─── Pastel tone palette ───
//
// Each tone is a soft, brand-friendly pair:
//   - bgFrom / bgTo  : two stops of a pastel gradient for the icon swatch
//   - fg             : a saturated mid-tone for the glyph itself
//   - ring           : the soft ring used on hover
//
// Picked to play well together at-a-glance and to match the app's clinical
// palette (cool neutrals + warm accents).

type Tone = {
  bgFrom: string;
  bgTo: string;
  fg: string;
  ring: string;
};

const TONES: Record<string, Tone> = {
  indigo: { bgFrom: '#eef2ff', bgTo: '#e0e7ff', fg: '#4f46e5', ring: 'rgba(99, 102, 241, 0.35)' },
  sky: { bgFrom: '#f0f9ff', bgTo: '#e0f2fe', fg: '#0284c7', ring: 'rgba(14, 165, 233, 0.35)' },
  cyan: { bgFrom: '#ecfeff', bgTo: '#cffafe', fg: '#0e7490', ring: 'rgba(6, 182, 212, 0.35)' },
  teal: { bgFrom: '#f0fdfa', bgTo: '#ccfbf1', fg: '#0f766e', ring: 'rgba(20, 184, 166, 0.35)' },
  emerald: {
    bgFrom: '#ecfdf5',
    bgTo: '#d1fae5',
    fg: '#059669',
    ring: 'rgba(16, 185, 129, 0.35)',
  },
  amber: { bgFrom: '#fffbeb', bgTo: '#fef3c7', fg: '#b45309', ring: 'rgba(245, 158, 11, 0.35)' },
  orange: { bgFrom: '#fff7ed', bgTo: '#ffedd5', fg: '#c2410c', ring: 'rgba(249, 115, 22, 0.35)' },
  rose: { bgFrom: '#fff1f2', bgTo: '#ffe4e6', fg: '#e11d48', ring: 'rgba(244, 63, 94, 0.35)' },
  pink: { bgFrom: '#fdf2f8', bgTo: '#fce7f3', fg: '#be185d', ring: 'rgba(236, 72, 153, 0.35)' },
  violet: { bgFrom: '#f5f3ff', bgTo: '#ede9fe', fg: '#7c3aed', ring: 'rgba(139, 92, 246, 0.35)' },
  purple: { bgFrom: '#faf5ff', bgTo: '#f3e8ff', fg: '#9333ea', ring: 'rgba(168, 85, 247, 0.35)' },
  slate: { bgFrom: '#f8fafc', bgTo: '#e2e8f0', fg: '#475569', ring: 'rgba(100, 116, 139, 0.35)' },
};

type ToneKey = keyof typeof TONES;

type LauncherItem = {
  label: string;
  icon: string;
  path: string;
  description?: string;
  tone: ToneKey;
};

type LauncherGroup = {
  title: string;
  items: LauncherItem[];
};

const LAUNCHER_GROUPS: LauncherGroup[] = [
  {
    title: 'Clinic Operations',
    items: [
      {
        label: 'Patients',
        icon: 'users',
        path: '/patients',
        description: 'Browse all patients',
        tone: 'indigo',
      },
      {
        // PRD.218 — Reusable prescription presets surfaced under the
        // Patients group because they are authored by clinicians for use
        // during encounters.
        label: 'Prescription Templates',
        icon: 'clipboard',
        path: '/patients/prescription-templates',
        description: 'Reusable prescription presets',
        tone: 'cyan',
      },
      {
        label: 'Queue',
        icon: 'list-ordered',
        path: '/clinic/queue',
        description: 'Today’s walk-in queue',
        tone: 'amber',
      },
      {
        label: 'Appointments',
        icon: 'calendar',
        path: '/appointments/month',
        description: 'Calendar & bookings',
        tone: 'violet',
      },
      {
        label: 'Pharmacy',
        icon: 'heart',
        path: '/inventory/pharmacy',
        description: 'Dispensing & stock',
        tone: 'rose',
      },
      {
        // PRD.216 — Global Invoice operational module.
        label: 'Invoices',
        icon: 'file-text',
        path: '/inventory/invoices',
        description: 'Settle & history',
        tone: 'emerald',
      },
    ],
  },
  {
    title: 'Clinical',
    items: [
      {
        label: 'Forms',
        icon: 'file-text',
        path: '/clinic/forms',
        description: 'Form templates',
        tone: 'teal',
      },
      {
        label: 'Charting Templates',
        icon: 'clipboard',
        path: '/clinic/charting-templates',
        description: 'Reusable chart blocks',
        tone: 'cyan',
      },
      {
        label: 'Prescription Print',
        icon: 'edit',
        path: '/clinic/prescription-print',
        description: 'Print layout settings',
        tone: 'emerald',
      },
      // PRD.238 — Invoice Print wizard.
      {
        label: 'Invoice Print',
        icon: 'file-text',
        path: '/clinic/invoice-print',
        description: 'Invoice print layout',
        tone: 'amber',
      },
      // PRD.239 — Purchase Order Print wizard. Sits with the other
      // print wizards in Clinical so all three are discoverable
      // together. Also surfaced in Settings → Inventory below.
      {
        label: 'Purchase Order Print',
        icon: 'file-text',
        path: '/inventory/purchase-order-print',
        description: 'PO print layout',
        tone: 'orange',
      },
      {
        label: 'Inventory',
        icon: 'layout-grid',
        path: '/inventory/items',
        description: 'Items & adjustments',
        tone: 'rose',
      },
    ],
  },
  {
    // PRD.226 follow-up — single "Settings" group surfaces every patient,
    // appointment and inventory configuration page so admins don't have
    // to remember where each lives. Ordered by domain (patient → appointment
    // → inventory). Queue has no settings pages today; add here when one
    // appears.
    title: 'Settings',
    items: [
      // ── Clinic-wide ──
      // PRD.230 — Tenant-level clinic profile, regional config, SMS
      // sender. Sits first because it's the broadest scope and admins
      // typically need it before per-domain settings make sense.
      {
        label: 'Clinic Settings',
        icon: 'gear',
        path: '/settings/clinic',
        description: 'Profile, regional & messaging',
        tone: 'slate',
      },

      // ── Patient ──
      {
        label: 'Patient Workspace',
        icon: 'sliders',
        path: '/clinic/patient-workspace',
        description: 'Workspace layout & overview',
        tone: 'slate',
      },
      {
        label: 'Patient Profile',
        icon: 'user',
        path: '/clinic/patient-profile',
        description: 'Profile field definitions',
        tone: 'sky',
      },
      {
        label: 'Patient Form',
        icon: 'clipboard',
        path: '/clinic/patient-form',
        description: 'Patient form schema',
        tone: 'purple',
      },
      {
        label: 'Vitals Definitions',
        icon: 'activity',
        path: '/clinic/vitals-definitions',
        description: 'Tracked vital signs',
        tone: 'pink',
      },
      {
        label: 'Intake Form Builder',
        icon: 'edit',
        path: '/clinic/intake-form',
        description: 'Patient intake form',
        tone: 'amber',
      },

      // ── Appointment ──
      {
        label: 'Appointment Calendar',
        icon: 'calendar',
        path: '/appointments/settings/calendar',
        description: 'Calendar display settings',
        tone: 'violet',
      },
      {
        label: 'Appointment Categories',
        icon: 'layout-grid',
        path: '/appointments/settings/categories',
        description: 'Categories & statuses',
        tone: 'cyan',
      },
      {
        label: 'Online Booking',
        icon: 'sparkles',
        path: '/appointments/settings/online-booking',
        description: 'Patient-facing online booking',
        tone: 'teal',
      },
      {
        label: 'Front Desk Booking',
        icon: 'clipboard',
        path: '/appointments/settings/front-desk-booking',
        description: 'Front desk booking flow',
        tone: 'indigo',
      },
      {
        label: 'Appointment Providers',
        icon: 'users',
        path: '/appointments/providers',
        description: 'Bookable providers',
        tone: 'emerald',
      },
      {
        label: 'Provider Availability',
        icon: 'calendar',
        path: '/appointments/provider-availability',
        description: 'Recurring availability & generated slots',
        tone: 'teal',
      },
      {
        label: 'Appointment Services',
        icon: 'list',
        path: '/appointments/services',
        description: 'Bookable services',
        tone: 'orange',
      },

      // ── Inventory ──
      {
        label: 'Inventory Suppliers',
        icon: 'building',
        path: '/inventory/suppliers',
        description: 'Supplier master list',
        tone: 'rose',
      },
      // PRD.239 — Purchase Order print wizard.
      {
        label: 'Purchase Order Print',
        icon: 'file-text',
        path: '/inventory/purchase-order-print',
        description: 'PO print layout settings',
        tone: 'amber',
      },
    ],
  },
];

// ─── 4-dots launcher icon (2x2 dot grid) ───
//
// Uses primary tones for a "branded" feel, not just a flat muted glyph.

function LauncherDotsIcon({ size = 22, colored = false }: { size?: number; colored?: boolean }) {
  const fill = colored ? undefined : 'currentColor';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ display: 'block', flexShrink: 0 }}
      aria-hidden="true"
    >
      <circle cx={7} cy={7} r={2.9} fill={colored ? '#4f46e5' : fill} />
      <circle cx={17} cy={7} r={2.9} fill={colored ? '#0ea5e9' : fill} />
      <circle cx={7} cy={17} r={2.9} fill={colored ? '#10b981' : fill} />
      <circle cx={17} cy={17} r={2.9} fill={colored ? '#f59e0b' : fill} />
    </svg>
  );
}

// ─── Trigger button ───

// Detect macOS at module load (safe in browser env). Used to show the
// right shortcut hint — ⌘ on macOS, Ctrl elsewhere.
const IS_MAC =
  typeof navigator !== 'undefined' &&
  /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent || '');
const SHORTCUT_HINT = IS_MAC ? '⌘ K' : 'Ctrl K';

export function AppLauncherButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={`Apps (${SHORTCUT_HINT})`}
      aria-label="Open app launcher"
      aria-keyshortcuts={IS_MAC ? 'Meta+K' : 'Control+K'}
      aria-expanded={open}
      style={{
        width: 36,
        height: 36,
        borderRadius: 'var(--radius-md)',
        background: open ? 'var(--bg-surface-2)' : 'transparent',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: open ? 'var(--color-primary)' : 'var(--text-muted)',
        cursor: 'pointer',
        padding: 0,
        transition: 'background 120ms ease, color 120ms ease',
      }}
    >
      <LauncherDotsIcon colored />
    </button>
  );
}

// ─── Overlay panel ───

export function AppLauncherOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Lock body scroll while open + close on Escape.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Reset the query each time the launcher closes so reopening starts
  // fresh, and autofocus the search input on open so the user can type
  // immediately without clicking. `autoFocus` doesn't fire reliably for
  // a re-mounted portal, so do it manually after the input renders.
  useEffect(() => {
    if (open) {
      // Tiny delay to let the portal mount before focusing.
      const id = requestAnimationFrame(() => searchRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    setQuery('');
    return undefined;
  }, [open]);

  const go = useCallback(
    (path: string) => {
      onClose();
      navigate(path);
    },
    [navigate, onClose],
  );

  // Filter groups by case-insensitive substring on label or description.
  // Items only — group titles are not searched (matching "Settings"
  // would surface every settings item which is the opposite of what
  // a quick filter is for).
  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LAUNCHER_GROUPS;
    return LAUNCHER_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          (item.description?.toLowerCase().includes(q) ?? false),
      ),
    })).filter((group) => group.items.length > 0);
  }, [query]);

  // Flatten the filtered tiles so Enter in the search input navigates
  // to the first match — fast keyboard pathway for power users.
  const firstMatch = filteredGroups[0]?.items[0] ?? null;

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="App launcher"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 'calc(var(--topbar-height, 56px) + 12px)',
      }}
    >
      {/* Dim/soften the background — page stays visible, secondary */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.32)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
        }}
      />

      {/* Launcher panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'min(1180px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - var(--topbar-height, 56px) - 32px)',
          borderRadius: 'var(--radius-lg, 14px)',
          background: 'var(--bg-surface, #fff)',
          boxShadow: '0 24px 64px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.08)',
          border: '1px solid var(--border, #e5e7eb)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-3, 12px)',
            padding: '18px 24px',
            borderBottom: '1px solid var(--border, #e5e7eb)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2, 8px)' }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 'var(--radius-md, 10px)',
                background: 'linear-gradient(135deg, #eef2ff 0%, #fdf2f8 100%)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LauncherDotsIcon colored />
            </span>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg, 18px)',
                fontWeight: 'var(--font-weight-semi, 600)',
                color: 'var(--text, #111827)',
                letterSpacing: '0.1px',
              }}
            >
              Apps
            </h2>
            {/* Keyboard shortcut hint — gray badge so users discover the
                chord without it competing visually with the title. */}
            <kbd
              aria-label={`Keyboard shortcut: ${SHORTCUT_HINT}`}
              style={{
                marginLeft: 6,
                display: 'inline-flex',
                alignItems: 'center',
                height: 22,
                padding: '0 8px',
                borderRadius: 'var(--radius-sm, 6px)',
                background: 'var(--bg-surface-2, #f1f5f9)',
                color: 'var(--text-muted, #6b7280)',
                border: '1px solid var(--border, #e5e7eb)',
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                fontFamily: 'inherit',
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap',
              }}
            >
              {SHORTCUT_HINT}
            </kbd>
          </div>

          {/* Search — pinned to the top-right of the header (sits between
              the title cluster and the close button). Autofocused on open
              so users can type immediately; Enter jumps to the first
              matching tile (fast keyboard path). */}
          <div
            style={{
              position: 'relative',
              width: 320,
              maxWidth: '50%',
              marginLeft: 'auto',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted, #6b7280)',
                display: 'inline-flex',
                pointerEvents: 'none',
              }}
            >
              <NavIcon name="search" />
            </span>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // Only jump-to-first on Enter when the user has actually
                // typed something — otherwise Enter on an empty field
                // would navigate to whatever happens to be the first
                // tile (Patients), which feels accidental.
                if (e.key === 'Enter' && query.trim() && firstMatch) {
                  e.preventDefault();
                  go(firstMatch.path);
                }
              }}
              placeholder="Search apps & settings…"
              aria-label="Search apps"
              style={{
                width: '100%',
                height: 36,
                padding: '0 36px 0 36px',
                borderRadius: 'var(--radius-md, 10px)',
                border: '1px solid var(--border, #e5e7eb)',
                background: 'var(--bg-surface-2, #f8fafc)',
                fontSize: 'var(--font-size-sm, 14px)',
                color: 'var(--text, #111827)',
                fontFamily: 'inherit',
                outline: 'none',
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  searchRef.current?.focus();
                }}
                aria-label="Clear search"
                title="Clear"
                // Skip in tab order so Tab from search lands directly on
                // the first tile instead of detouring through this affordance.
                tabIndex={-1}
                style={{
                  position: 'absolute',
                  right: 6,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 24,
                  height: 24,
                  borderRadius: 'var(--radius-sm, 6px)',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-muted, #6b7280)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              >
                <NavIcon name="x" />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close app launcher"
            title="Close (Esc)"
            // Skipped in tab order so Tab from the search goes straight
            // to the tile grid. The launcher already closes on Escape and
            // on backdrop click; keyboard users keep both paths.
            tabIndex={-1}
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-md, 10px)',
              border: 'none',
              background: 'transparent',
              color: 'var(--text-muted, #6b7280)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>

        {/* Body — grouped sections */}
        <div
          style={{
            padding: '20px 24px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-6, 24px)',
          }}
        >
          {filteredGroups.length === 0 ? (
            <div
              style={{
                padding: '40px 16px',
                textAlign: 'center',
                color: 'var(--text-muted, #6b7280)',
                fontSize: 'var(--font-size-sm, 14px)',
              }}
            >
              No apps match <strong style={{ color: 'var(--text, #111827)' }}>“{query}”</strong>.
            </div>
          ) : (
            filteredGroups.map((group) => (
              <section key={group.title}>
                <h3
                  style={{
                    margin: '0 0 var(--sp-3, 12px)',
                    fontSize: 'var(--font-size-xs, 12px)',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted, #6b7280)',
                  }}
                >
                  {group.title}
                </h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                    gap: 'var(--sp-3, 12px)',
                  }}
                >
                  {group.items.map((item) => (
                    <LauncherTile key={item.path} item={item} onSelect={() => go(item.path)} />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function LauncherTile({ item, onSelect }: { item: LauncherItem; onSelect: () => void }) {
  const tone = TONES[item.tone];
  const [focused, setFocused] = useState(false);
  const swatchStyle: CSSProperties = {
    width: 44,
    height: 44,
    borderRadius: 'var(--radius-md, 10px)',
    background: `linear-gradient(135deg, ${tone.bgFrom} 0%, ${tone.bgTo} 100%)`,
    color: tone.fg,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: `inset 0 0 0 1px ${tone.ring}`,
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3, 12px)',
        padding: 'var(--sp-3, 12px) var(--sp-4, 16px)',
        borderRadius: 'var(--radius-lg, 14px)',
        // Keyboard focus uses the tile's tone color so the user can see
        // which result Tab has landed on. Native Enter on a focused
        // <button> already fires our onClick (= go(item.path)), so we
        // get keyboard-activation for free.
        border: `1px solid ${focused ? tone.fg : 'var(--border, #e5e7eb)'}`,
        boxShadow: focused ? `0 0 0 3px ${tone.ring}` : 'none',
        background: 'var(--bg-surface, #fff)',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        minHeight: 72,
        outline: 'none',
        transition: 'border-color 120ms, box-shadow 120ms, transform 120ms',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = tone.fg;
        e.currentTarget.style.boxShadow = `0 4px 14px ${tone.ring}`;
      }}
      onMouseLeave={(e) => {
        if (focused) {
          // Don't strip the focus ring on mouseleave if the button is
          // still keyboard-focused.
          e.currentTarget.style.borderColor = tone.fg;
          e.currentTarget.style.boxShadow = `0 0 0 3px ${tone.ring}`;
        } else {
          e.currentTarget.style.borderColor = 'var(--border, #e5e7eb)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      <span style={swatchStyle}>
        <NavIcon name={item.icon} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm, 14px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            color: 'var(--text, #111827)',
            lineHeight: 'var(--line-height-tight, 1.2)',
          }}
        >
          {item.label}
        </span>
        {item.description ? (
          <span
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--text-muted, #6b7280)',
              lineHeight: 'var(--line-height-base, 1.4)',
            }}
          >
            {item.description}
          </span>
        ) : null}
      </span>
    </button>
  );
}
